import { useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { ReactLenis } from "lenis/react";
import { ArrowLeft, ArrowRight, Phone } from "lucide-react";
import { Toaster } from "@/components/ui/sonner";
import Nav from "./Nav";
import Booking from "./Booking";
import Footer from "./Footer";
import MobileCallBar from "./MobileCallBar";
import Reveal from "./Reveal";
import { trackConversion, trackPageView } from "@/lib/analytics";

function SectionCta({ type }) {
  if (type === "prices") {
    return (
      <Link to="/" className="btn-brutal-dark mt-5 !text-lg" data-testid="topic-cta-prices">
        SEE ALL SWAP-OUT PRICES <ArrowRight className="w-4 h-4" strokeWidth={2.5} />
      </Link>
    );
  }
  return null;
}

export default function TopicPage({ topic }) {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = topic.metaTitle;
    const desc = document.querySelector('meta[name="description"]');
    if (desc) desc.setAttribute("content", topic.metaDesc);
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.setAttribute("href", `${process.env.REACT_APP_CANONICAL_BASE}/${topic.slug}`);
    trackPageView(window.location.pathname);
  }, [topic]);

  const goBook = useCallback(() => {
    trackConversion("book_click");
    document.getElementById("book")?.scrollIntoView({ behavior: "smooth" });
  }, []);

  return (
    <ReactLenis root options={{ lerp: 0.09, smoothWheel: true }}>
      <div className="App bg-white text-tpeco-ink font-body overflow-x-clip" data-testid={`topic-page-${topic.slug}`}>
        <Nav onBook={goBook} />
        <header className="bg-tpeco-blue text-white py-16 sm:py-20 border-b-4 border-tpeco-ink grain-overlay">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <Link to="/" className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-white/80 hover:text-white transition-colors" data-testid="topic-back-link">
              <ArrowLeft className="w-4 h-4" /> All hot water help
            </Link>
            <span className="block mt-4"><span className="chip-brutal !border-white bg-white/15 text-white">SOMETHING ELSE? WE CAN HELP!</span></span>
            <h1 className="mt-4 font-display uppercase text-6xl sm:text-7xl lg:text-8xl leading-[0.95]" data-testid="topic-heading">
              {topic.title[0]}<br /><span className="bg-tpeco-pink px-3 rounded-2xl inline-block">{topic.title[1]}</span>
            </h1>
            <p className="mt-5 max-w-2xl text-base sm:text-lg font-semibold text-white/90" data-testid="topic-intro">{topic.intro}</p>
            <div className="mt-7 flex flex-wrap gap-3">
              <button onClick={goBook} className="btn-brutal" data-testid="topic-book-button">BOOK AN ASSESSMENT</button>
              <a href="tel:0475776555" className="btn-brutal-light" data-testid="topic-call-button">
                <Phone className="w-5 h-5" strokeWidth={2.5} /> 0475 776 555
              </a>
            </div>
          </div>
        </header>

        <main className="max-w-4xl mx-auto px-4 sm:px-6 py-14 sm:py-20 space-y-12">
          {topic.sections.map((s, i) => (
            <Reveal key={s.h} i={i % 3} data-testid={`topic-section-${i}`}>
              <h2 className="font-display uppercase text-3xl sm:text-4xl leading-tight">{s.h}</h2>
              {s.price && (
                <span className="chip-brutal bg-tpeco-pink border-tpeco-pink text-white mt-3">{s.price}</span>
              )}
              {s.p?.map((para, j) => (
                <p key={j} className="mt-4 text-base font-semibold text-neutral-700">{para}</p>
              ))}
              {s.list && (
                <ul className="mt-4 space-y-2">
                  {s.list.map((item) => (
                    <li key={item} className="flex gap-2 text-base font-semibold text-neutral-700">
                      <ArrowRight className="w-4 h-4 mt-1 text-tpeco-blue shrink-0" strokeWidth={2.5} /> {item}
                    </li>
                  ))}
                </ul>
              )}
              {s.p2?.map((para, j) => (
                <p key={j} className="mt-4 text-base font-semibold text-neutral-700">{para}</p>
              ))}
              {s.cta === "book" && (
                <button onClick={goBook} className="btn-brutal mt-5 !text-lg" data-testid={`topic-section-book-${i}`}>
                  BOOK A FREE ASSESSMENT <ArrowRight className="w-4 h-4" strokeWidth={2.5} />
                </button>
              )}
              <SectionCta type={s.cta} />
            </Reveal>
          ))}

          {topic.faq && (
            <Reveal data-testid="topic-faq">
              <h2 className="font-display uppercase text-3xl sm:text-4xl">QUICK ANSWERS</h2>
              <div className="mt-5 space-y-4">
                {topic.faq.map((f) => (
                  <div key={f.q} className="card-brutal p-5">
                    <h3 className="font-display uppercase text-xl">{f.q}</h3>
                    <p className="mt-2 text-sm font-semibold text-neutral-700">{f.a}</p>
                  </div>
                ))}
              </div>
            </Reveal>
          )}
        </main>

        <Booking selectedUnit={null} suburb={null} />
        <Footer />
        <MobileCallBar onBook={goBook} />
        <Toaster position="top-center" richColors />
      </div>
    </ReactLenis>
  );
}
